// artifacts-report-lookup (report@3): ההתנהגות של שדה "בחירה עם חיפוש" בלי React — טהור, נבדק ב-vitest (test/lookup-field.test.tsx).
// הקלדה → חיפוש אחד אחרי 250ms של שקט (תשובה ישנה נזרקת); פוקוס על שדה ריק → q:"" פעם אחת; ↑/↓ נעים, Enter בוחר, Esc סוגר;
// מצבים: "מחפש…", "אין תוצאות", "יש עוד…" (truncated), 429 → "יותר מדי חיפושים…", שגיאת שרת — הטקסט שלה.
import type { LookupResponse } from './contract.ts';
import { T } from './strings.ts';

export type LookupRow = LookupResponse['rows'][number];
export type LookupStatus = { kind: 'searching' | 'empty' | 'more' | 'error'; textHe: string };

export const LOOKUP_DEBOUNCE_MS = 250;

export class LookupSearch {
  rows: LookupRow[] = [];
  open = false;
  active = -1;
  status: LookupStatus | null = null;
  private seq = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private focusedEmpty = false;

  constructor(
    private fetch: (q: string) => Promise<LookupResponse>,
    private onUpdate: () => void,
    private debounceMs = LOOKUP_DEBOUNCE_MS,
  ) {}

  /** הטקסט השתנה: "מחפש…" מיד, החיפוש עצמו אחרי השהיה (מקליד ברצף = קריאה אחת). */
  type(text: string) {
    if (this.timer) clearTimeout(this.timer);
    this.seq++; // תשובה שבדרך כבר לא רלוונטית
    this.status = { kind: 'searching', textHe: T.form.searching };
    this.timer = setTimeout(() => void this.search(text.trim()), this.debounceMs);
    this.onUpdate();
  }

  /** פוקוס: שדה ריק — הרשימה הראשונה פעם אחת (הפעולה מחליטה מה להחזיר ל-q ריק); אחרת פותח שוב תוצאות שכבר יש. */
  focus(text: string, picked: boolean) {
    if (!text.trim() && !this.focusedEmpty) {
      this.focusedEmpty = true;
      void this.search('');
    } else if (this.rows.length && !picked) {
      this.open = true;
      this.onUpdate();
    }
  }

  async search(q: string) {
    const n = ++this.seq;
    this.status = { kind: 'searching', textHe: T.form.searching };
    this.onUpdate();
    try {
      const r = await this.fetch(q);
      if (n !== this.seq) return; // הוקלד משהו חדש בינתיים
      this.rows = r.rows;
      this.active = -1; // שום שורה לא מסומנת עד ↓/↑ (Enter בלי סימון שולח את הטופס, והטופס אומר "בחר מהרשימה")
      this.open = true;
      this.status = !r.rows.length ? { kind: 'empty', textHe: T.form.noResults } : r.truncated ? { kind: 'more', textHe: T.form.moreResults } : null;
    } catch (e) {
      if (n !== this.seq) return;
      this.rows = [];
      this.open = false;
      // ApiError של lib/api.ts (status + textHe) — בלי לייבא אותו (המודול הזה נבדק ב-node בלי auth)
      const x = (e ?? {}) as { status?: unknown; textHe?: unknown };
      this.status = { kind: 'error', textHe: x.status === 429 ? T.form.tooManySearches : typeof x.textHe === 'string' && x.textHe ? x.textHe : T.network };
    }
    this.onUpdate();
  }

  /** מקלדת: מחזיר את השורה שנבחרה ב-Enter; handled — המקש נצרך (preventDefault); null — לא שלנו (Enter בלי רשימה שולח את הטופס). */
  key(k: string): LookupRow | 'handled' | null {
    if (k === 'ArrowDown' || k === 'ArrowUp') {
      if (!this.rows.length) return 'handled';
      this.open = true;
      const n = this.rows.length;
      this.active = k === 'ArrowDown' ? (this.active + 1) % n : this.active < 0 ? n - 1 : (this.active - 1 + n) % n;
      this.onUpdate();
      return 'handled';
    }
    if (k === 'Enter' && this.open && this.active >= 0 && this.rows[this.active]) return this.pick(this.active);
    if (k === 'Escape' && this.open) {
      this.close();
      return 'handled';
    }
    return null;
  }

  pick(i: number): LookupRow {
    const r = this.rows[i]!;
    this.open = false;
    this.status = null;
    this.onUpdate();
    return r;
  }

  hover(i: number) {
    this.active = i;
    this.onUpdate();
  }

  close() {
    if (!this.open) return;
    this.open = false;
    this.onUpdate();
  }

  dispose() {
    if (this.timer) clearTimeout(this.timer);
    this.seq++;
  }
}
